import { Box, Flex, Input, Menu, Text, ScrollArea, Tooltip, Loader } from '@mantine/core';
import { createStyles } from '@mantine/emotion';
import { TbChevronDown, TbFile, TbFiles } from 'react-icons/tb';
import ExcelIcon from '../../../../assets/icons/excel.svg?react';
import PdfIcon from '../../../../assets/icons/pdf.svg?react';
import { useMemo, useRef, useState } from 'react';
import { useClickOutside } from '@mantine/hooks';
import { idCreator } from '../../../Project/utils/idCreator';

const useStyles = createStyles(theme => ({
  select: {
    border: `1px solid ${theme.colors.gray[1]}`,
    borderRadius: '4px',
    minWidth: '183px'
  },
  uploadType: {
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4
  }
}));

export function FileSelect({
  idPrefix,
  label,
  value,
  options = [],
  placeholder,
  onChange,
  disabledOptions = [],
  color,
  isLoading = false
}) {
  const inputRef = useRef();
  const textRef = useRef();
  const ref = useClickOutside(() => {
    setOpened(false);
  });
  const [term, setTerm] = useState('');
  const { classes } = useStyles();
  const [opened, setOpened] = useState(false);
  const [inputWidth, setInputWidth] = useState(200);
  const rootId = idPrefix || 'data-extraction-file-select';

  const truncateText = text => {
    if (!text) return '';
    return text.length > 40 ? `${text.slice(0, 40)}...` : text;
  };

  const handleTermChange = e => {
    setTerm(e.target.value);
  };

  const handleMenuItemClick = item => () => {
    onChange(item);
    setOpened(false);
  };

  const handleMenuOpen = () => {
    onOpenedMenu(true);
  };

  const renderFormatIcon = item => {
    const Icon = item?.format === 'pdf' ? PdfIcon : ExcelIcon;
    const testId = item?.format === 'pdf' ? 'file-format-icon-pdf' : 'file-format-icon-excel';

    return <Icon data-testid={testId} style={{ minWidth: '16px' }} />;
  };

  const renderSelectOptions = item => (
    <Tooltip maw="400px" multiline key={item.id} label={item.name} disabled={item.name.length < 40}>
      <Menu.Item
        id={`${rootId}-option-${item.id || idCreator(item.name)}`}
        onClick={handleMenuItemClick(item)}
        disabled={disabledOptions.includes(item.id || item.name)}
        w="100%"
        p="8px"
        bg={item.id === value?.id ? 'gray.1' : ''}
      >
        <Flex
          id={`${rootId}-option-row-${item.id || idCreator(item.name)}`}
          align="center"
          justify="space-between"
        >
          <Flex
            id={`${rootId}-option-left-${item.id || idCreator(item.name)}`}
            gap="10px"
            align="center"
          >
            {renderFormatIcon(item)}
            <Flex
              id={`${rootId}-option-texts-${item.id || idCreator(item.name)}`}
              direction="column"
            >
              <Text
                id={`${rootId}-option-name-${item.id || idCreator(item.name)}`}
                maw="300px"
                c="black"
                fz="14px"
                lh="20px"
              >
                {truncateText(item.name)}
              </Text>

              {item?.input_fund && (
                <Tooltip
                  position="bottom"
                  label={item.input_fund.name}
                  disabled={item.input_fund.name.length < 40}
                  maw="400px"
                  multiline
                >
                  <Text
                    id={`${rootId}-option-input-fund-${item.id || idCreator(item.name)}`}
                    maw="320px"
                    fz="12px"
                    c="gray.7"
                    lh="16px"
                  >
                    {truncateText(item.input_fund.name)}
                  </Text>
                </Tooltip>
              )}
            </Flex>
          </Flex>

          <Tooltip label={!item.is_per_fund ? 'Multi-Upload' : 'Single-upload'}>
            <Box
              id={`${rootId}-option-type-${item.id || idCreator(item.name)}`}
              className={classes.uploadType}
              bg="#F1F3F5"
            >
              {!item.is_per_fund ? (
                <TbFiles size={16} color="#5C5F66" />
              ) : (
                <TbFile size={16} color="#5C5F66" />
              )}
            </Box>
          </Tooltip>
        </Flex>
      </Menu.Item>
    </Tooltip>
  );

  const filteredOptions = useMemo(() => {
    if (!options || options.length === 0) return [];

    if (value?.name === term) return options;

    if (!term) return options;

    return options.filter(item => item.name.toLowerCase().includes(term.toLowerCase()));
  }, [options, term, value]);

  const onOpenedMenu = val => {
    setOpened(value);
    setTerm(value?.name);
    setInputWidth(textRef.current?.offsetWidth);
    if (val)
      setTimeout(() => {
        inputRef.current?.focus();
      }, 500);
  };

  return (
    <Menu id={`${rootId}-menu`} shadow="md" opened={opened}>
      <Menu.Target ref={ref}>
        <Box id={`${rootId}-root`}>
          <Text id={`${rootId}-label`} mb="4" fz="12px" fw="600" color="#25262B">
            {label}
          </Text>
          <Flex
            id={`${rootId}-trigger`}
            align="center"
            justify="space-between"
            className={classes.select}
            pos="relative"
            maw="240px"
            style={{
              border: !value || color ? `1px solid ${color}` : `1px solid #CED4DA`,
              cursor: opened ? 'default' : 'pointer'
            }}
            gap="8px"
            onClick={handleMenuOpen}
          >
            {opened ? (
              <Input
                id={`${rootId}-input`}
                fz="12px"
                lh="17px"
                placeholder={placeholder}
                value={term}
                onChange={handleTermChange}
                w={inputWidth}
                ref={inputRef}
                styles={{
                  input: {
                    fontSize: '12px',
                    lineHeight: '17px',
                    height: 'auto',
                    minHeight: 'auto',
                    border: 'none',
                    padding: '3.5px 28px 3.5px 12px'
                  }
                }}
              />
            ) : (
              <Flex
                id={`${rootId}-value-row`}
                align="center"
                w="100%"
                gap="8px"
                pl="12px"
                pr="28px"
              >
                {value?.name && <Box id={`${rootId}-value-icon`}>{renderFormatIcon(value)}</Box>}
                <Tooltip disabled={value?.name?.length < 36 || !value?.name} label={value?.name}>
                  <Text
                    id={`${rootId}-value`}
                    fz="12px"
                    lh="17px"
                    style={{
                      color: color ? color : value?.name ? '#000' : '#909296'
                    }}
                    w="100%"
                    py="3.5px"
                    ref={textRef}
                    display={opened ? 'none' : 'block'}
                    truncate="end"
                  >
                    {value?.name || placeholder}
                  </Text>
                </Tooltip>
              </Flex>
            )}

            <Flex id={`${rootId}-chevron`} pos="absolute" right="12px" top="6px" miw="12px">
              {isLoading ? (
                <Loader id={`${rootId}-loader`} size={12} />
              ) : (
                <TbChevronDown
                  id={`${rootId}-chevron-icon`}
                  fontSize="12px"
                  color={color ? color : value?.name ? '#000' : '#909296'}
                />
              )}
            </Flex>
          </Flex>
        </Box>
      </Menu.Target>

      <Menu.Dropdown id={`${rootId}-dropdown`} p="8px" w="410px">
        <ScrollArea.Autosize scrollbarSize={14} scrollHideDelay={500} mah="58vh">
          {filteredOptions.map(item => renderSelectOptions(item))}
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  );
}
